import { useEffect, useLayoutEffect, useRef, type DragEvent as RDragEvent, type PointerEvent as RPointerEvent, type RefObject } from 'react';

/** Where the window is scrolled to. */
export interface Scroll {
  left: number;
  top: number;
}

/** A point of the window kept where it is through a zoom, or the scroll to land on once the new zoom is drawn. */
export type Keep = { x: number; y: number } | ((from: Scroll, before: number, next: number) => Scroll);

/**
 * The scroll that keeps the drawing's point under `at` (a point of the window) where it is from zoom `before` to `next`,
 * the drawing starting `origin` into what the window scrolls over (a margin of the window's own pixels round it).
 */
export function keptScroll(from: Scroll, at: { x: number; y: number }, before: number, next: number, origin = { x: 0, y: 0 }): Scroll {
  return {
    left: ((from.left + at.x - origin.x) / before) * next + origin.x - at.x,
    top: ((from.top + at.y - origin.y) / before) * next + origin.y - at.y,
  };
}

export interface Paper {
  /** Spread on the scrolling window: a press on the paper that moves pans it, and no press in it is ever the browser's own drag. */
  bind: {
    onPointerDown: (e: RPointerEvent<HTMLElement>) => void;
    onPointerMove: (e: RPointerEvent<HTMLElement>) => void;
    onPointerUp: (e: RPointerEvent<HTMLElement>) => void;
    onPointerCancel: () => void;
    onDragStart: (e: RDragEvent<HTMLElement>) => void;
  };
  /** Zoom to `next`, keeping a point of the window where it is (its middle unless said), or scrolling where `keep` says. */
  zoomTo: (next: number, keep?: Keep) => void;
}

/**
 * Every large drawing handles the same way (Jason, 2026-10-03: "we need
 * scroll wheel zoom on the tree"; 2026-10-04: "i also cant pan in the tree
 * builder"): the paper is dragged to move round it, the wheel zooms round the
 * pointer and sideways it pans, and any other zoom (a button) keeps the middle
 * of the window where it is. A turn of the wheel that began scrolling the page
 * goes on scrolling it when the drawing comes under the pointer: the wheel
 * zooms once the page has been still a moment. A press that does not move is
 * a click (`onTap`), and the pointer is held only once it moves, so a double
 * click still lands where it was made. No press on the paper or on anything on
 * it is ever the browser's own drag of a selection, a link or a picture (P-34).
 *
 * Its zoom is the caller's (`zoom`, `setZoom`), drawn by scaling what is
 * inside the window; the scroll that keeps a point in place is applied once
 * the new zoom is drawn, and turns of the wheel before then build on it.
 */
export function usePaper(
  scroller: RefObject<HTMLElement | null>,
  opts: {
    zoom: number;
    setZoom: (zoom: number) => void;
    min: number;
    max: number;
    /** The press began on the paper itself, not on something on it. */
    isPaper: (target: EventTarget) => boolean;
    /** A press on the paper that did not move. */
    onTap?: () => void;
    /** Where the drawing starts in what the window scrolls over, when a margin of the window's pixels is round it. */
    origin?: () => { x: number; y: number };
  },
): Paper {
  const { zoom } = opts;
  const latest = useRef(opts);
  useEffect(() => {
    latest.current = opts;
  });
  const drawn = useRef(zoom);
  const wanted = useRef(zoom);
  const pending = useRef<Scroll | null>(null);

  // The new zoom is drawn: scroll to keep what was asked for, else the middle.
  useLayoutEffect(() => {
    const el = scroller.current;
    const before = drawn.current;
    drawn.current = zoom;
    wanted.current = zoom;
    if (!el) return;
    const to = pending.current;
    pending.current = null;
    if (to) el.scrollTo({ left: to.left, top: to.top, behavior: 'instant' });
    else if (before !== zoom) {
      const kept = keptScroll({ left: el.scrollLeft, top: el.scrollTop }, { x: el.clientWidth / 2, y: el.clientHeight / 2 }, before, zoom, latest.current.origin?.());
      el.scrollTo({ ...kept, behavior: 'instant' });
    }
  }, [zoom, scroller]);

  const zoomTo = (next: number, keep?: Keep) => {
    const el = scroller.current;
    const before = wanted.current;
    if (!el) {
      wanted.current = next;
      latest.current.setZoom(next);
      return;
    }
    const from = pending.current ?? { left: el.scrollLeft, top: el.scrollTop };
    const at = keep ?? { x: el.clientWidth / 2, y: el.clientHeight / 2 };
    const to = typeof at === 'function' ? at(from, before, next) : keptScroll(from, at, before, next, latest.current.origin?.());
    if (Math.abs(next - drawn.current) < 0.0005 && Math.abs(next - before) < 0.0005) {
      pending.current = null;
      el.scrollTo({ left: to.left, top: to.top, behavior: 'instant' });
      return;
    }
    pending.current = to;
    wanted.current = next;
    latest.current.setZoom(next);
  };
  const zoomRef = useRef(zoomTo);
  useEffect(() => {
    zoomRef.current = zoomTo;
  });

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    let pageMoved = 0;
    const onScroll = () => {
      pageMoved = performance.now();
    };
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      if (performance.now() - pageMoved < 350) return;
      const { min, max } = latest.current;
      const before = wanted.current;
      const delta = e.deltaMode === 1 ? e.deltaY * 33 : e.deltaMode === 2 ? e.deltaY * 400 : e.deltaY;
      const next = Number(Math.min(max, Math.max(min, before * Math.exp(-delta * 0.0015))).toFixed(3));
      if (Math.abs(next - before) < 0.001) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomRef.current(next, { x: e.clientX - r.left, y: e.clientY - r.top });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('wheel', onWheel);
      window.removeEventListener('scroll', onScroll);
    };
  }, [scroller]);

  const pan = useRef<{ x: number; y: number; left: number; top: number; moved: boolean; pointer: number } | null>(null);
  const bind = {
    onPointerDown: (e: RPointerEvent<HTMLElement>) => {
      if (e.button !== 0 || !latest.current.isPaper(e.target)) return;
      const el = e.currentTarget;
      pan.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop, moved: false, pointer: e.pointerId };
    },
    onPointerMove: (e: RPointerEvent<HTMLElement>) => {
      const p = pan.current;
      if (!p || p.pointer !== e.pointerId) return;
      const dx = e.clientX - p.x;
      const dy = e.clientY - p.y;
      if (!p.moved) {
        if (Math.hypot(dx, dy) < 4) return;
        p.moved = true;
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          // A pointer the browser no longer knows: the pan still follows it over the window.
        }
      }
      e.currentTarget.scrollLeft = p.left - dx;
      e.currentTarget.scrollTop = p.top - dy;
    },
    onPointerUp: (e: RPointerEvent<HTMLElement>) => {
      const p = pan.current;
      pan.current = null;
      if (p && p.pointer === e.pointerId && !p.moved) latest.current.onTap?.();
    },
    onPointerCancel: () => {
      pan.current = null;
    },
    // A press inside text selected on the page (a pan's own sweep, a select all) would be the browser's drag of the
    // selection, a picture of the drawing under the hand, and its pointer cancelled: the circle or note pressed never
    // moved, and the paper never panned (P-34, Jason, 2026-10-27: "you have to click a few times to get it to take,
    // otherwise it seems to drag the whole image"). A drag from outside still drops on the paper.
    onDragStart: (e: RDragEvent<HTMLElement>) => e.preventDefault(),
  };
  return { bind, zoomTo };
}

/** Hides a window's scroll bars: the paper is dragged instead (Jason, 2026-10-03: "not have scroll bars if we can avoid it"). */
export const NO_SCROLLBARS = '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden';
